import PropTypes from 'prop-types'
import { useEffect, useRef, useState } from 'react'
import InputMask from 'react-input-mask'
import { useSelector } from 'react-redux'
import { getUnique } from '../../helpers/common'
import {
  compareTwoDateTime,
  FORMAT,
  formatDateTime,
  formatDateTimeLocal,
} from '../../helpers/datetime'
import EventEmitter, {
  SET_FROM_DATE,
  SET_SINGLE_DATE,
  SET_TO_DATE,
  SHOW_SINGLE_DATE,
} from '../../helpers/EventEmitter'
import { UseI18n } from '../hooks/useI18n'
import { UseTimeZone } from '../hooks/useTimeZone'
import { typeInputTyping } from './constants'
import { convertResultDate, getDateByLocale } from './helper'

const MASK = '99/99/9999'
const placeholderChar = '_'
const defaultText = '__/__/____'

const InputTypingDate = ({
  date,
  onChange,
  minDate,
  maxDate,
  style,
  typeInput,
  placeholder,
  defaultFocus,
}) => {
  const timezone = UseTimeZone()

  const locale = useSelector((state) => state.i18n.locale)
  const placeholderDate = UseI18n('common.datePicker.INPUT_TYPING_PLACEHOLDER')

  const eventRef = useRef(null)
  const idUnique = useRef(getUnique())

  const [mask, setMask] = useState('')
  const [text, setText] = useState('')
  const [isFocus, setIsFocus] = useState(defaultFocus)

  useEffect(() => {
    if (date) {
      setMask(MASK)
      setText(formatDateTimeLocal(date, FORMAT.DATE_INPUT))
    } else {
      setText('')
    }
  }, [date])

  const onInputChange = (e) => {
    eventRef.current = e
    setText(e.target.value)
  }

  const onKeyDown = (e) => {
    if (e.key === 'Enter') {
      EventEmitter.dispatch(SHOW_SINGLE_DATE, false)
      setIsFocus(false)
    }
  }

  useEffect(() => {
    setMask(text.length ? MASK : '')
    if (!eventRef.current) return
    if (text.length === 10 && !text.includes(placeholderChar)) {
      let date = getDateByLocale(text)
      if (compareTwoDateTime(date, minDate) === -1) date = new Date(minDate)
      if (compareTwoDateTime(date, maxDate) === 1) date = new Date(maxDate)
      date = convertResultDate(date, timezone)
      setText(formatDateTimeLocal(date, FORMAT.DATE_INPUT))
      onChange(date, eventRef.current)

      const dateConvert = new Date(
        formatDateTime(date, FORMAT.DATE_PICKER, locale, timezone),
      )

      if (typeInput === typeInputTyping.SINGLE_DATE) {
        EventEmitter.dispatch(SET_SINGLE_DATE, dateConvert)
      } else if (typeInput === typeInputTyping.FROM_DATE) {
        EventEmitter.dispatch(SET_FROM_DATE, dateConvert)
      } else if (typeInput === typeInputTyping.TO_DATE) {
        EventEmitter.dispatch(SET_TO_DATE, dateConvert)
      }
    }
    if (text === defaultText) {
      onChange(null, eventRef.current)
    }
  }, [text, timezone])

  const focus = () => {
    setIsFocus(true)
    setTimeout(
      () =>
        document
          .getElementById(idUnique.current)
          .getElementsByTagName('input')[0]
          .focus(),
      200,
    )
  }

  const blur = () => setIsFocus(false)

  return (
    <div id={idUnique.current}>
      {isFocus ? (
        <InputMask
          mask={mask}
          value={text}
          onChange={onInputChange}
          onKeyDown={onKeyDown}
          style={style}
          onBlur={blur}
          autoFocus={defaultFocus}
        />
      ) : (
        <input
          style={style}
          type={text}
          placeholder={placeholder || placeholderDate}
          readOnly
          onClick={focus}
          value={
            date ? formatDateTime(date, FORMAT.DATE, locale, timezone) : ''
          }
        />
      )}
    </div>
  )
}

InputTypingDate.propTypes = {
  onChange: PropTypes.func.isRequired,
  date: PropTypes.oneOfType([PropTypes.string, PropTypes.instanceOf(Date)]),
  minDate: PropTypes.oneOfType([PropTypes.string, PropTypes.instanceOf(Date)]),
  maxDate: PropTypes.oneOfType([PropTypes.string, PropTypes.instanceOf(Date)]),
  style: PropTypes.object,
  typeInput: PropTypes.string.isRequired,
  placeholder: PropTypes.string,
  defaultFocus: PropTypes.bool,
}

InputTypingDate.defaultProps = {
  minDate: new Date(1950, 0, 1),
  maxDate: new Date(2050, 0, 0),
  defaultFocus: false,
}

export default InputTypingDate
